import { useId, type ReactNode } from 'react';
import { useT } from '../i18n';

/**
 * Form primitives.
 *
 * Every control carries a one-line hint, because the parameters here are
 * VOSviewer's and a label like "fractional counting" means nothing on its own.
 * The hint is part of the control, not an optional extra, which is why it is
 * baked into the wrapper rather than left to each call site.
 */

interface FieldProps {
  label: string;
  hint?: string;
  optional?: boolean;
  error?: string;
  children: (props: { id: string; describedBy: string | undefined }) => ReactNode;
}

export function Field({ label, hint, optional, error, children }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  const t = useT();

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
        {optional ? <span className="field__optional">{t('common.optional')}</span> : null}
      </label>
      {children({ id, describedBy })}
      {hint ? (
        <p className="field__hint" id={hintId}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p className="field__error" id={errorId}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

export interface TextFieldProps {
  label: string;
  hint?: string;
  optional?: boolean;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: string;
}

export function TextField({
  label,
  hint,
  optional,
  placeholder,
  value,
  onChange,
  disabled,
  error,
}: TextFieldProps) {
  return (
    <Field label={label} hint={hint} optional={optional} error={error}>
      {({ id, describedBy }) => (
        <input
          id={id}
          className="input"
          type="text"
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </Field>
  );
}

export interface NumberFieldProps {
  label: string;
  hint?: string;
  optional?: boolean;
  /** `null` means "left empty", which several parameters treat as no limit. */
  value: number | null;
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  error?: string;
  placeholder?: string;
}

export function NumberField({
  label,
  hint,
  optional,
  value,
  onChange,
  min,
  max,
  step,
  disabled,
  error,
  placeholder,
}: NumberFieldProps) {
  return (
    <Field label={label} hint={hint} optional={optional} error={error}>
      {({ id, describedBy }) => (
        <input
          id={id}
          className="input input--number"
          type="number"
          inputMode="decimal"
          value={value === null ? '' : String(value)}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          placeholder={placeholder}
          aria-describedby={describedBy}
          onChange={(event) => {
            const raw = event.target.value.trim();
            if (raw === '') {
              onChange(null);
              return;
            }
            const parsed = Number(raw);
            onChange(Number.isFinite(parsed) ? parsed : null);
          }}
        />
      )}
    </Field>
  );
}

export interface SelectOption<T extends string> {
  value: T;
  label: string;
}

export interface SelectFieldProps<T extends string> {
  label: string;
  hint?: string;
  value: T;
  options: readonly SelectOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
}

export function SelectField<T extends string>({
  label,
  hint,
  value,
  options,
  onChange,
  disabled,
}: SelectFieldProps<T>) {
  return (
    <Field label={label} hint={hint}>
      {({ id, describedBy }) => (
        <select
          id={id}
          className="select"
          value={value}
          disabled={disabled}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.value as T)}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}

export interface CheckboxFieldProps {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

export function CheckboxField({ label, hint, checked, onChange, disabled }: CheckboxFieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  return (
    <div className="field">
      <label className="checkbox" htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          aria-describedby={hintId}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span className="checkbox__text">
          <span>{label}</span>
          {hint ? (
            <span className="field__hint" id={hintId}>
              {hint}
            </span>
          ) : null}
        </span>
      </label>
    </div>
  );
}

export interface RadioCardOption<T extends string> {
  value: T;
  title: string;
  description?: string;
}

export interface RadioCardsProps<T extends string> {
  legend: string;
  name: string;
  value: T;
  options: readonly RadioCardOption<T>[];
  onChange: (value: T) => void;
  wide?: boolean;
}

export function RadioCards<T extends string>({
  legend,
  name,
  value,
  options,
  onChange,
  wide,
}: RadioCardsProps<T>) {
  return (
    <fieldset className="field" style={{ border: 0, margin: 0, padding: 0 }}>
      <legend className="field__label">{legend}</legend>
      <div className={wide ? 'choices choices--wide' : 'choices'}>
        {options.map((option) => (
          <label
            key={option.value}
            className={option.value === value ? 'choice choice--selected' : 'choice'}
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={option.value === value}
              onChange={() => onChange(option.value)}
            />
            <span>
              <span className="choice__title">{option.title}</span>
              {option.description ? (
                <span className="choice__desc">{option.description}</span>
              ) : null}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export interface SegmentedProps<T extends string> {
  label: string;
  value: T;
  options: readonly SelectOption<T>[];
  onChange: (value: T) => void;
}

export function Segmented<T extends string>({ label, value, options, onChange }: SegmentedProps<T>) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className="segmented__button"
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
